import React, { useState, useEffect, useRef } from 'react'
import { X, Brain, Send, Loader, CheckCircle, AlertCircle, Sparkles, ChevronDown, ChevronUp } from 'lucide-react'
import { tenantFetch } from '../lib/tenant-fetch'
import { useTenant } from '../contexts/TenantContext'

interface AIAgentModalProps {
  isOpen: boolean
  onClose: () => void
  initialContext?: {
    page?: string
    selectedItems?: string[]
    prefilledMessage?: string
  }
}

interface DisplayMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  toolCalls?: DisplayToolCall[]
}

interface DisplayToolCall {
  id: string
  toolName: string
  arguments: Record<string, any>
  status: 'pending' | 'executing' | 'completed' | 'failed'
  result?: any
  error?: string
  actionId?: string // For undo tracking
  undoable?: boolean // Can this action be undone?
  undone?: boolean // Has this been undone?
}

const AIAgentModal: React.FC<AIAgentModalProps> = ({ isOpen, onClose, initialContext }) => {
  const { currentTenant } = useTenant()
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [inputText, setInputText] = useState('')
  const [loading, setLoading] = useState(false)
  const [provider, setProvider] = useState<'claude' | 'openai' | 'gemini'>('claude')
  const [availableProviders, setAvailableProviders] = useState<('claude' | 'openai' | 'gemini')[]>([])
  const [undoingActionId, setUndoingActionId] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<{ toolName: string; onConfirm: () => void } | null>(null)
  const [expandedToolCalls, setExpandedToolCalls] = useState<Set<string>>(new Set())
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const toggleToolCallExpanded = (toolCallId: string) => {
    setExpandedToolCalls(prev => {
      const newSet = new Set(prev)
      if (newSet.has(toolCallId)) {
        newSet.delete(toolCallId)
      } else {
        newSet.add(toolCallId)
      }
      return newSet
    })
  }

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Load available providers on mount
  useEffect(() => {
    if (isOpen && currentTenant) {
      loadAvailableProviders()
    }
  }, [isOpen, currentTenant])

  // Set prefilled message if provided
  useEffect(() => {
    if (initialContext?.prefilledMessage && inputText === '') {
      setInputText(initialContext.prefilledMessage)
    }
  }, [initialContext])

  const loadAvailableProviders = async () => {
    // For Phase 1, we'll just assume Claude is available
    // Phase 3 will add a proper endpoint to check available providers
    setAvailableProviders(['claude'])
    setProvider('claude')
  }

  const sendMessage = async () => {
    if (!inputText.trim() || loading || !currentTenant) return

    const userMessage = inputText.trim()
    setInputText('')
    setLoading(true)

    // Add user message to display
    const tempUserMessage: DisplayMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }
    setMessages(prev => [...prev, tempUserMessage])

    try {
      const response = await tenantFetch('/api/agent/chat', {
        tenantId: currentTenant.id,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId,
          message: userMessage,
          provider,
          autoExecute: true,
          context: initialContext
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }))
        throw new Error(errorData.error || errorData.message || 'Failed to send message')
      }

      const data = await response.json()

      // Update conversation ID
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId)
      }

      // Add assistant message
      const assistantMessage: DisplayMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.response.message,
        timestamp: new Date().toISOString(),
        toolCalls: data.response.toolCalls
      }
      setMessages(prev => [...prev, assistantMessage])

    } catch (error: any) {
      console.error('Error sending message:', error)

      // Add error message
      const errorMessage: DisplayMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}`,
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const clearConversation = () => {
    setConversationId(null)
    setMessages([])
    setInputText('')
  }

  const handleUndo = async (actionId: string) => {
    if (!currentTenant || undoingActionId) return

    setUndoingActionId(actionId)

    try {
      const response = await tenantFetch('/api/agent/undo', {
        tenantId: currentTenant.id,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actionId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Undo failed' }))
        throw new Error(errorData.error || 'Failed to undo action')
      }

      const data = await response.json()

      // Update messages to mark action as undone
      setMessages(prev => prev.map(msg => ({
        ...msg,
        toolCalls: msg.toolCalls?.map(tc =>
          tc.actionId === actionId ? { ...tc, undone: true } : tc
        )
      })))

      // Add success message
      const successMessage: DisplayMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `✓ Action undone successfully. ${data.message || ''}`,
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, successMessage])

    } catch (error: any) {
      console.error('Error undoing action:', error)

      // Add error message
      const errorMessage: DisplayMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Failed to undo action: ${error.message}`,
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setUndoingActionId(null)
    }
  }

  const isDangerousAction = (toolName: string): boolean => {
    const dangerousActions = ['delete_', 'archive_', 'remove_']
    return dangerousActions.some(action => toolName.includes(action))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">AI Assistant</h2>
              <p className="text-xs text-gray-500">Powered by {provider === 'claude' ? 'Claude' : provider}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Provider selector (Phase 1: just Claude) */}
            {availableProviders.length > 1 && (
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as any)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
                disabled={messages.length > 0}
              >
                {availableProviders.map(p => (
                  <option key={p} value={p}>{p === 'claude' ? 'Claude' : p === 'openai' ? 'OpenAI' : 'Gemini'}</option>
                ))}
              </select>
            )}
            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                New Chat
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                How can I help you today?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                I can help you with knowledge base status, compliance badges, security features, and more.
              </p>

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
                <button
                  onClick={() => setInputText('What is the status of my knowledge base?')}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                >
                  📊 KB Status
                </button>
                <button
                  onClick={() => setInputText('List all my compliance badges')}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                >
                  🏅 List Badges
                </button>
                <button
                  onClick={() => setInputText('Show me my security features')}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                >
                  🔒 Security Features
                </button>
                <button
                  onClick={() => setInputText('List active subprocessors')}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                >
                  🏢 Subprocessors
                </button>
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-lg p-4 ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{msg.content}</div>

                  {/* Tool calls visualization */}
                  {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {msg.toolCalls.map((tool) => {
                        const isExpanded = expandedToolCalls.has(tool.id)
                        const resultSummary = tool.result?.message ||
                          (tool.result?.count !== undefined ? `Found ${tool.result.count} item${tool.result.count !== 1 ? 's' : ''}` : null)

                        return (
                          <div
                            key={tool.id}
                            className="bg-white rounded border border-gray-200 p-3"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900 capitalize">
                                  {tool.toolName.replace(/_/g, ' ')}
                                </span>
                                {tool.status === 'completed' && (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                )}
                                {tool.status === 'failed' && (
                                  <AlertCircle className="h-4 w-4 text-red-600" />
                                )}
                                {tool.status === 'executing' && (
                                  <Loader className="h-4 w-4 text-blue-600 animate-spin" />
                                )}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                tool.status === 'completed' ? 'bg-green-100 text-green-800' :
                                tool.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {tool.status}
                              </span>
                            </div>

                            {tool.error && (
                              <div className="space-y-2">
                                <div className="text-sm text-red-600 font-semibold">
                                  Error: {tool.error}
                                </div>
                                {tool.error.includes('Permission denied') && (
                                  <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2 space-y-1">
                                    <div className="font-semibold text-yellow-800">Debug Info:</div>
                                    <div className="text-yellow-700">
                                      This error means your JWT token doesn't have the required 'admin' role.
                                    </div>
                                    <div className="text-yellow-700">
                                      Current token: Check DevTools → Application → Local Storage → auth-token
                                    </div>
                                    <div className="text-yellow-700 font-semibold mt-2">
                                      Fix: Log out, then log back in to get a fresh token with the correct role.
                                    </div>
                                    <div className="text-yellow-600 mt-2 text-xs">
                                      Recent changes were deployed at: {new Date().toLocaleTimeString()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Summary for completed actions */}
                            {resultSummary && tool.status === 'completed' && (
                              <div className="text-xs text-gray-600 italic mb-1">
                                {resultSummary}
                              </div>
                            )}

                            {/* Expandable detailed results */}
                            {tool.result && tool.status === 'completed' && (
                              <>
                                <button
                                  onClick={() => toggleToolCallExpanded(tool.id)}
                                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1 mt-1"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp className="h-3 w-3" />
                                      <span>Hide details</span>
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-3 w-3" />
                                      <span>Show details</span>
                                    </>
                                  )}
                                </button>
                                {isExpanded && (
                                  <div className="text-xs text-gray-600 mt-2 max-h-48 overflow-y-auto bg-gray-50 rounded p-2">
                                    <pre className="whitespace-pre-wrap break-words font-mono">
                                      {JSON.stringify(tool.result, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Undo button for undoable actions */}
                            {tool.actionId && tool.undoable && tool.status === 'completed' && !tool.undone && (
                              <button
                                onClick={() => handleUndo(tool.actionId!)}
                                disabled={undoingActionId === tool.actionId}
                                className="mt-2 text-xs px-3 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {undoingActionId === tool.actionId ? 'Undoing...' : 'Undo'}
                              </button>
                            )}

                            {/* Undone indicator */}
                            {tool.undone && (
                              <div className="mt-2 text-xs text-gray-500 italic">
                                ↩ This action has been undone
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className="text-xs opacity-70 mt-2">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Loader className="h-4 w-4 animate-spin text-gray-600" />
                  <span className="text-sm text-gray-600">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-3">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Shift+Enter for new line)"
              className="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={2}
              disabled={loading}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !inputText.trim() || !currentTenant}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Send className="h-4 w-4" />
              <span>Send</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>

        {/* Confirmation Modal for Dangerous Actions */}
        {confirmAction && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 rounded-lg">
            <div className="bg-white rounded-lg p-6 max-w-md shadow-xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Dangerous Action</h3>
              </div>
              <p className="text-gray-700 mb-6">
                You are about to execute: <strong>{confirmAction.toolName.replace(/_/g, ' ')}</strong>
                <br />
                <br />
                This action may delete or modify important data. While you can undo this action within 90 days,
                please confirm you want to proceed.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmAction.onConfirm()
                    setConfirmAction(null)
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AIAgentModal
